import type { EduDueData } from '../../services/eduFeeService'

export function getTodayDateIso(): string {
  return new Intl.DateTimeFormat('en-CA').format(new Date())
}

/**
 * Determines whether a fee due is overdue.
 */
export function isDueOverdue(due: EduDueData, todayStr = getTodayDateIso()): boolean {
  if (due.status === 'PAID' || due.status === 'WAIVED') return false
  if (due.status === 'OVERDUE') return true
  if (due.dueDate && due.dueDate < todayStr && (due.balanceMinor ?? 0) > 0) return true
  return false
}

/**
 * Calculates days overdue. Returns 0 if not overdue.
 */
export function calculateDaysOverdue(dueDate?: string | null, todayTime = Date.now()): number {
  if (!dueDate) return 0
  const dueTime = new Date(dueDate).getTime()
  if (isNaN(dueTime)) return 0
  const diffMs = todayTime - dueTime
  return Math.max(0, Math.floor(diffMs / 86400000))
}

/**
 * Sorts dues with overdue dues first, then by dueDate ascending.
 */
export function sortDuesOverdueFirst(dues: EduDueData[], todayStr = getTodayDateIso()): EduDueData[] {
  return [...dues].sort((a, b) => {
    const aOverdue = isDueOverdue(a, todayStr)
    const bOverdue = isDueOverdue(b, todayStr)
    if (aOverdue && !bOverdue) return -1
    if (!aOverdue && bOverdue) return 1
    return (a.dueDate || '').localeCompare(b.dueDate || '')
  })
}

/**
 * Creates an idempotency request ID for recording fee payment.
 */
export function createFeePaymentRequestId(): string {
  return `payreq_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

export interface PaymentValidationInput {
  method: 'CASH' | 'UPI' | 'BANK'
  reference?: string
  amountReceived: number
  balanceMinor: number
  paidOn: string
  todayStr?: string
  currency: string | null
}

export interface PaymentValidationResult {
  valid: boolean
  errorKey?:
    | 'NLSEducationFeesErrorCurrencyNotSet'
    | 'NLSEducationFeesErrZeroAmount'
    | 'NLSEducationFeesErrAmountExceedsBalance'
    | 'NLSEducationFeesErrReferenceRequired'
    | 'NLSEducationFeesErrFutureDate'
}

/**
 * Validates fee payment recording input.
 */
export function validatePaymentInput(input: PaymentValidationInput): PaymentValidationResult {
  if (!input.currency) {
    return { valid: false, errorKey: 'NLSEducationFeesErrorCurrencyNotSet' }
  }
  if (input.amountReceived <= 0) {
    return { valid: false, errorKey: 'NLSEducationFeesErrZeroAmount' }
  }
  const inputMinor = Math.round(input.amountReceived * 100)
  if (inputMinor > input.balanceMinor) {
    return { valid: false, errorKey: 'NLSEducationFeesErrAmountExceedsBalance' }
  }
  if ((input.method === 'UPI' || input.method === 'BANK') && (!input.reference || !input.reference.trim())) {
    return { valid: false, errorKey: 'NLSEducationFeesErrReferenceRequired' }
  }
  const today = input.todayStr || getTodayDateIso()
  if (input.paidOn > today) {
    return { valid: false, errorKey: 'NLSEducationFeesErrFutureDate' }
  }
  return { valid: true }
}
